import { useTranslation } from "react-i18next";
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { LoadingSkeleton } from "../../../components/LoadingSkeleton";
import { PaymentStatusButton } from "../../../components/PaymentStatusButton";
import { TableAllWastesCell } from "../../../components/TableAllWastesCell";
import { TableCellWithRightArrow } from "../../../components/TableCellWithRightArrow";
import { TableDateCell } from "../../../components/TableDateCell";
import { TableDownloadCYVisitReceiptCell } from "../../../components/TableDownloadCYVisitReceiptCell";
import { TablePriceCell } from "../../../components/TablePriceCell";
import { TableWeightCell } from "../../../components/TableWeightCell";
import { updateYardVisitIsPaidStatusThunk } from "../../../reducers/collectionYardsVisitsDetailReducer";
import { IUpdateVisitIsPaidStatusRequest } from "../../../types/collectionYards";
import { IColumnValuePair } from "../../../types/table";
import { getTableColumnUtils } from "../../../utils/tables/getTableColumnUtils";
import { columnLabels, ColumnType } from "./CollectionYardsTableColumnTypes";
import { CollectionYardsTableHeaderCell } from "./CollectionYardsTableHeaderCell";

const columnFlexValuePairs: IColumnValuePair<ColumnType>[] = [
  {
    name: ColumnType.created,
    value: 2,
  },
  {
    name: ColumnType.locationName,
    value: 2,
  },
  {
    name: ColumnType.wcpid,
    value: 2,
  },
  {
    name: ColumnType.collectionYardName,
    value: 3,
  },
  {
    name: ColumnType.wasteTypes,
    value: 3,
  },
  {
    name: ColumnType.priceSum,
    value: 2,
  },
  {
    name: ColumnType.wasteSum,
    value: 2,
  },
  {
    name: ColumnType.paidAt,
    value: 2,
  },
];

const columnMinWidthValuePairs: IColumnValuePair<ColumnType>[] = [
  {
    name: ColumnType.paidAt,
    value: 255,
  },
];

export const useGetCollectionYardsTableColumns = () => {
  const { t } = useTranslation();
  const { getFlex, getWidth } = getTableColumnUtils();

  const columns: GridColDef[] = columnLabels.map((label) => {
    const columnObject: GridColDef = {
      field: label,
      headerName: t(label),
      type: "string",
      renderHeader: (params: GridColumnHeaderParams) => {
        return <CollectionYardsTableHeaderCell params={params} />;
      },
      flex: getFlex(label, columnFlexValuePairs),
      minWidth: getWidth(label, columnMinWidthValuePairs),
    };

    columnObject.renderCell = (params: GridRenderCellParams) => {
      if (params.row.isMocked) {
        return <LoadingSkeleton />;
      }

      switch (label) {
        case ColumnType.created:
          return <TableDateCell params={params} />;
        case ColumnType.wasteTypes:
          return <TableAllWastesCell params={params} />;
        case ColumnType.priceSum:
          return <TablePriceCell params={params} />;
        case ColumnType.wasteSum:
          return <TableWeightCell params={params} />;
        case ColumnType.paidAt: {
          const isPaid = Boolean(params.row.paidAt);
          const payload: IUpdateVisitIsPaidStatusRequest = {
            collection_yard_visit_id: params.row.visitId,
            isPaid: !isPaid,
          };
          return (
            <>
              <PaymentStatusButton
                isPaid={isPaid}
                paidAt={params.row.paidAt}
                translationKey={"collectionYardVisit.paymentModal"}
                payload={payload}
                thunk={updateYardVisitIsPaidStatusThunk}
                isTableCell={true}
                isVisible={params.row.priceSum > 0}
              />
              <TableCellWithRightArrow
                mainComponent={
                  <TableDownloadCYVisitReceiptCell visitId={params.row.visitId} priceSum={params.row.priceSum} />
                }
              />
            </>
          );
        }

        default:
          break;
      }
    };

    return columnObject;
  });

  return columns;
};
